import React, { useState } from 'react';
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ALMData, TimeHorizon } from './types';
import ALMPositionTableRow from './table/ALMPositionTableRow';
import NetPositionRow from './table/NetPositionRow';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { TableCell } from '@/components/ui/table';
import { formatCurrency } from '@/components/borrowings/debt/utils';
import { ALMContract } from './contracts/types';
import ContractTableRow from './contracts/ContractTableRow';
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface ALMPositionTableProps {
  almData: ALMData;
  currentHorizons: TimeHorizon[];
  totals: {
    assets: Record<string, number>;
    liabilities: Record<string, number>;
    equity: Record<string, number>;
    netPosition: Record<string, number>;
  };
  onOpenBorrowSheet: (amount: number, horizonLabel: string) => void;
  contracts: ALMContract[];
  onContractClick: (contract: ALMContract) => void;
}

const ALMPositionTable: React.FC<ALMPositionTableProps> = ({
  almData,
  currentHorizons,
  totals,
  onOpenBorrowSheet,
  contracts,
  onContractClick
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [contractSearches, setContractSearches] = useState<Record<string, string>>({});
  const [contractPages, setContractPages] = useState<Record<string, number>>({});
  const contractsPerPage = 5;
  
  const assetTotal = { name: "Assets", values: totals.assets };
  const liabilityTotal = { name: "Liabilities", values: totals.liabilities };
  const equityTotal = { name: "Equity", values: totals.equity };
  
  // Define parent-child relationships for proper indentation
  const parentCategories = {
    "Loan Assets": ["Vehicle Loans", "Personal Loans", "SME Loans", "Gold Loans"],
    "Fixed Deposits": ["Bank FDs", "Corporate FDs", "Certificate of Deposits", "Treasury Bills"],
    "Cash and Bank": ["Current Accounts", "Savings Accounts"],
    "Non-Convertible Debentures": ["Secured NCDs", "Unsecured NCDs", "Market Linked NCDs"],
    "Term Loans": ["Bank Term Loans", "NBFC Term Loans", "External Commercial Borrowings", "Refinance Loans"],
    "Working Capital Loans": ["Cash Credit", "Overdraft Facilities", "Short-term Credit Lines"],
    "Share Capital": ["Equity Share Capital", "Preference Share Capital", "Compulsory Convertible Debentures"]
  };
  
  // Map subcategories to their parents
  const isSubcategory = (name: string): boolean => {
    return Object.values(parentCategories).some(children => children.includes(name));
  };
  
  const getParentCategory = (name: string): string | null => {
    for (const [parent, children] of Object.entries(parentCategories)) {
      if (children.includes(name)) {
        return parent;
      }
    }
    return null;
  };
  
  // Group contracts by type
  const contractsByType = React.useMemo(() => {
    const grouped: Record<string, ALMContract[]> = {};
    contracts.forEach(contract => {
      if (!grouped[contract.type]) {
        grouped[contract.type] = [];
      }
      grouped[contract.type].push(contract);
    });
    return grouped;
  }, [contracts]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
    
    // Reset the pagination for this category when expanding
    if (!expandedCategories[category]) {
      setContractPages(prev => ({
        ...prev,
        [category]: 1
      }));
    }
  };
  
  const handleSearchChange = (category: string, value: string) => {
    setContractSearches(prev => ({
      ...prev,
      [category]: value
    }));
    setContractPages(prev => ({
      ...prev,
      [category]: 1
    }));
  };
  
  const getFilteredContracts = (category: string, contractsList: ALMContract[]) => {
    const search = contractSearches[category] || "";
    if (!search) return contractsList;
    
    return contractsList.filter(contract => 
      contract.name.toLowerCase().includes(search.toLowerCase()) ||
      contract.reference?.toLowerCase().includes(search.toLowerCase())
    );
  };
  
  const getPaginatedContracts = (category: string, contractsList: ALMContract[]) => {
    const filteredContracts = getFilteredContracts(category, contractsList);
    const currentPage = contractPages[category] || 1;
    const startIndex = (currentPage - 1) * contractsPerPage;
    return {
      contracts: filteredContracts.slice(startIndex, startIndex + contractsPerPage),
      totalPages: Math.ceil(filteredContracts.length / contractsPerPage),
      totalCount: filteredContracts.length
    };
  };
  
  const handlePageChange = (category: string, page: number) => {
    setContractPages(prev => ({
      ...prev,
      [category]: page
    }));
  };
  
  // Render pagination UI
  const renderPagination = (category: string, totalPages: number) => {
    if (totalPages <= 1) return null;
    
    const currentPage = contractPages[category] || 1;
    
    return (
      <TableRow>
        <TableCell colSpan={currentHorizons.length + 1}>
          <div className="flex justify-between items-center p-2">
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={() => handlePageChange(category, Math.max(1, currentPage - 1))}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
                
                {totalPages <= 5 ? (
                  // Show all pages if 5 or fewer
                  [...Array(totalPages)].map((_, i) => (
                    <PaginationItem key={i}>
                      <PaginationLink 
                        isActive={currentPage === i + 1}
                        onClick={() => handlePageChange(category, i + 1)}
                      >
                        {i + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))
                ) : (
                  // Show limited pages with ellipses
                  <>
                    <PaginationItem>
                      <PaginationLink 
                        isActive={currentPage === 1}
                        onClick={() => handlePageChange(category, 1)}
                      >
                        1
                      </PaginationLink>
                    </PaginationItem>
                    
                    {currentPage > 3 && (
                      <PaginationItem>
                        <PaginationLink>...</PaginationLink>
                      </PaginationItem>
                    )}
                    
                    {currentPage > 2 && (
                      <PaginationItem>
                        <PaginationLink 
                          onClick={() => handlePageChange(category, currentPage - 1)}
                        >
                          {currentPage - 1}
                        </PaginationLink>
                      </PaginationItem>
                    )}
                    
                    {currentPage !== 1 && currentPage !== totalPages && (
                      <PaginationItem>
                        <PaginationLink isActive>
                          {currentPage}
                        </PaginationLink>
                      </PaginationItem>
                    )}
                    
                    {currentPage < totalPages - 1 && (
                      <PaginationItem>
                        <PaginationLink 
                          onClick={() => handlePageChange(category, currentPage + 1)}
                        >
                          {currentPage + 1}
                        </PaginationLink>
                      </PaginationItem>
                    )}
                    
                    {currentPage < totalPages - 2 && (
                      <PaginationItem>
                        <PaginationLink>...</PaginationLink>
                      </PaginationItem>
                    )}
                    
                    <PaginationItem>
                      <PaginationLink 
                        isActive={currentPage === totalPages}
                        onClick={() => handlePageChange(category, totalPages)}
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  </>
                )}
                
                <PaginationItem>
                  <PaginationNext 
                    onClick={() => handlePageChange(category, Math.min(totalPages, currentPage + 1))}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </TableCell>
      </TableRow>
    );
  };
  
  // Render search UI
  const renderContractSearch = (category: string) => {
    return (
      <TableRow>
        <TableCell colSpan={currentHorizons.length + 1}>
          <div className="flex items-center p-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search contracts..."
                value={contractSearches[category] || ''}
                onChange={(e) => handleSearchChange(category, e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[300px]">Category</TableHead>
          {currentHorizons.map(horizon => (
            <TableHead key={horizon.id} className="text-right">
              {horizon.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      
      <TableBody>
        {/* Assets section */}
        <ALMPositionTableRow 
          item={assetTotal} 
          currentHorizons={currentHorizons} 
          isHeader 
          className="font-medium bg-muted/20"
        />
        
        {almData.assets.map((asset, index) => {
          // Skip rendering subcategories here since we'll handle them in the parent category logic
          if (isSubcategory(asset.name)) {
            return null;
          }
          
          const isParent = parentCategories[asset.name]?.length > 0;
          
          return (
            <React.Fragment key={`asset-${index}`}>
              <TableRow 
                key={`asset-${index}`}
                className="cursor-pointer hover:bg-muted/40"
                onClick={() => toggleCategory(`asset-${asset.name}`)}
              >
                <TableCell className={isParent ? "font-semibold" : "pl-6 text-muted-foreground"}>
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6 p-0"
                    >
                      {expandedCategories[`asset-${asset.name}`] ? 
                        <ChevronDown className="h-4 w-4" /> : 
                        <ChevronRight className="h-4 w-4" />}
                    </Button>
                    <span>{asset.name}</span>
                  </div>
                </TableCell>
                
                {currentHorizons.map(horizon => (
                  <TableCell 
                    key={horizon.id} 
                    className={`text-right ${isParent ? "font-semibold" : "text-muted-foreground"}`}
                  >
                    {formatCurrency(asset.values[horizon.id] || 0)}
                  </TableCell>
                ))}
              </TableRow>
              
              {/* Show contracts when category is expanded */}
              {expandedCategories[`asset-${asset.name}`] && (
                <>
                  {/* If it's a parent, render the child categories first */}
                  {isParent && parentCategories[asset.name]?.map((childName, childIndex) => {
                    const childItem = almData.assets.find(item => item.name === childName);
                    if (!childItem) return null;
                    
                    return (
                      <React.Fragment key={`asset-child-${childIndex}`}>
                        <TableRow 
                          className="cursor-pointer hover:bg-muted/40"
                          onClick={() => toggleCategory(`asset-${childName}`)}
                        >
                          <TableCell className="pl-12 text-muted-foreground">
                            <div className="flex items-center space-x-2">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6 p-0"
                              >
                                {expandedCategories[`asset-${childName}`] ? 
                                  <ChevronDown className="h-4 w-4" /> : 
                                  <ChevronRight className="h-4 w-4" />}
                              </Button>
                              <span>{childName}</span>
                            </div>
                          </TableCell>
                          
                          {currentHorizons.map(horizon => (
                            <TableCell 
                              key={horizon.id} 
                              className="text-right text-muted-foreground"
                            >
                              {formatCurrency(childItem.values[horizon.id] || 0)}
                            </TableCell>
                          ))}
                        </TableRow>
                        
                        {/* Expand child contracts if child category is expanded */}
                        {expandedCategories[`asset-${childName}`] && (
                          <>
                            {renderContractSearch(`asset-${childName}`)}
                            {(() => {
                              const childContracts = getContractsForCategory(childName, contractsByType);
                              const { contracts: paginatedContracts, totalPages, totalCount } = 
                                getPaginatedContracts(`asset-${childName}`, childContracts);
                              
                              return (
                                <>
                                  {paginatedContracts.length > 0 ? (
                                    <>
                                      {paginatedContracts.map(contract => (
                                        <ContractTableRow 
                                          key={contract.id}
                                          contract={contract}
                                          currentHorizons={currentHorizons}
                                          onClick={() => onContractClick(contract)}
                                        />
                                      ))}
                                      <TableRow>
                                        <TableCell colSpan={currentHorizons.length + 1} className="text-sm text-right text-muted-foreground pr-4">
                                          Showing {paginatedContracts.length} of {totalCount} contracts
                                        </TableCell>
                                      </TableRow>
                                      {renderPagination(`asset-${childName}`, totalPages)}
                                    </>
                                  ) : (
                                    <TableRow>
                                      <TableCell colSpan={currentHorizons.length + 1} className="text-center py-4 text-muted-foreground">
                                        {contractSearches[`asset-${childName}`] ? 
                                          "No matching contracts found" : 
                                          "No contracts available for this category"}
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </>
                              );
                            })()}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                  
                  {/* If it's not a parent with subcategories, just render its contracts */}
                  {!isParent && (
                    <>
                      {renderContractSearch(`asset-${asset.name}`)}
                      {(() => {
                        const assetContracts = getContractsForCategory(asset.name, contractsByType);
                        const { contracts: paginatedContracts, totalPages, totalCount } = 
                          getPaginatedContracts(`asset-${asset.name}`, assetContracts);
                        
                        return (
                          <>
                            {paginatedContracts.length > 0 ? (
                              <>
                                {paginatedContracts.map(contract => (
                                  <ContractTableRow 
                                    key={contract.id}
                                    contract={contract}
                                    currentHorizons={currentHorizons}
                                    onClick={() => onContractClick(contract)}
                                  />
                                ))}
                                <TableRow>
                                  <TableCell colSpan={currentHorizons.length + 1} className="text-sm text-right text-muted-foreground pr-4">
                                    Showing {paginatedContracts.length} of {totalCount} contracts
                                  </TableCell>
                                </TableRow>
                                {renderPagination(`asset-${asset.name}`, totalPages)}
                              </>
                            ) : (
                              <TableRow>
                                <TableCell colSpan={currentHorizons.length + 1} className="text-center py-4 text-muted-foreground">
                                  {contractSearches[`asset-${asset.name}`] ? 
                                    "No matching contracts found" : 
                                    "No contracts available for this category"}
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}
                </>
              )}
            </React.Fragment>
          );
        })}
        
        {/* Liabilities section */}
        <ALMPositionTableRow 
          item={liabilityTotal} 
          currentHorizons={currentHorizons} 
          isHeader 
          className="font-medium bg-muted/20"
        />
        
        {almData.liabilities.map((liability, index) => {
          // Skip rendering subcategories here since we'll handle them in the parent category logic
          if (isSubcategory(liability.name)) {
            return null;
          }
          
          const isParent = parentCategories[liability.name]?.length > 0;
          
          return (
            <React.Fragment key={`liability-${index}`}>
              <TableRow 
                key={`liability-${index}`}
                className="cursor-pointer hover:bg-muted/40"
                onClick={() => toggleCategory(`liability-${liability.name}`)}
              >
                <TableCell className={isParent ? "font-semibold" : "pl-6 text-muted-foreground"}>
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6 p-0"
                    >
                      {expandedCategories[`liability-${liability.name}`] ? 
                        <ChevronDown className="h-4 w-4" /> : 
                        <ChevronRight className="h-4 w-4" />}
                    </Button>
                    <span>{liability.name}</span>
                  </div>
                </TableCell>
                
                {currentHorizons.map(horizon => (
                  <TableCell 
                    key={horizon.id} 
                    className={`text-right ${isParent ? "font-semibold" : "text-muted-foreground"}`}
                  >
                    {formatCurrency(liability.values[horizon.id] || 0)}
                  </TableCell>
                ))}
              </TableRow>
              
              {/* Show subcategories and contracts when category is expanded */}
              {expandedCategories[`liability-${liability.name}`] && (
                <>
                  {/* If it's a parent, render the child categories first */}
                  {isParent && parentCategories[liability.name]?.map((childName, childIndex) => {
                    const childItem = almData.liabilities.find(item => item.name === childName);
                    if (!childItem) return null;
                    
                    return (
                      <React.Fragment key={`liability-child-${childIndex}`}>
                        <TableRow 
                          className="cursor-pointer hover:bg-muted/40"
                          onClick={() => toggleCategory(`liability-${childName}`)}
                        >
                          <TableCell className="pl-12 text-muted-foreground">
                            <div className="flex items-center space-x-2">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6 p-0"
                              >
                                {expandedCategories[`liability-${childName}`] ? 
                                  <ChevronDown className="h-4 w-4" /> : 
                                  <ChevronRight className="h-4 w-4" />}
                              </Button>
                              <span>{childName}</span>
                            </div>
                          </TableCell>
                          
                          {currentHorizons.map(horizon => (
                            <TableCell 
                              key={horizon.id} 
                              className="text-right text-muted-foreground"
                            >
                              {formatCurrency(childItem.values[horizon.id] || 0)}
                            </TableCell>
                          ))}
                        </TableRow>
                        
                        {/* Expand child contracts if child category is expanded */}
                        {expandedCategories[`liability-${childName}`] && (
                          <>
                            {renderContractSearch(`liability-${childName}`)}
                            {(() => {
                              const childContracts = getContractsForCategory(childName, contractsByType);
                              const { contracts: paginatedContracts, totalPages, totalCount } = 
                                getPaginatedContracts(`liability-${childName}`, childContracts);
                              
                              return (
                                <>
                                  {paginatedContracts.length > 0 ? (
                                    <>
                                      {paginatedContracts.map(contract => (
                                        <ContractTableRow 
                                          key={contract.id}
                                          contract={contract}
                                          currentHorizons={currentHorizons}
                                          onClick={() => onContractClick(contract)}
                                        />
                                      ))}
                                      <TableRow>
                                        <TableCell colSpan={currentHorizons.length + 1} className="text-sm text-right text-muted-foreground pr-4">
                                          Showing {paginatedContracts.length} of {totalCount} contracts
                                        </TableCell>
                                      </TableRow>
                                      {renderPagination(`liability-${childName}`, totalPages)}
                                    </>
                                  ) : (
                                    <TableRow>
                                      <TableCell colSpan={currentHorizons.length + 1} className="text-center py-4 text-muted-foreground">
                                        {contractSearches[`liability-${childName}`] ? 
                                          "No matching contracts found" : 
                                          "No contracts available for this category"}
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </>
                              );
                            })()}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                  
                  {/* If it's not a parent with subcategories, just render its contracts */}
                  {!isParent && (
                    <>
                      {renderContractSearch(`liability-${liability.name}`)}
                      {(() => {
                        const liabilityContracts = getContractsForCategory(liability.name, contractsByType);
                        const { contracts: paginatedContracts, totalPages, totalCount } = 
                          getPaginatedContracts(`liability-${liability.name}`, liabilityContracts);
                        
                        return (
                          <>
                            {paginatedContracts.length > 0 ? (
                              <>
                                {paginatedContracts.map(contract => (
                                  <ContractTableRow 
                                    key={contract.id}
                                    contract={contract}
                                    currentHorizons={currentHorizons}
                                    onClick={() => onContractClick(contract)}
                                  />
                                ))}
                                <TableRow>
                                  <TableCell colSpan={currentHorizons.length + 1} className="text-sm text-right text-muted-foreground pr-4">
                                    Showing {paginatedContracts.length} of {totalCount} contracts
                                  </TableCell>
                                </TableRow>
                                {renderPagination(`liability-${liability.name}`, totalPages)}
                              </>
                            ) : (
                              <TableRow>
                                <TableCell colSpan={currentHorizons.length + 1} className="text-center py-4 text-muted-foreground">
                                  {contractSearches[`liability-${liability.name}`] ? 
                                    "No matching contracts found" : 
                                    "No contracts available for this category"}
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}
                </>
              )}
            </React.Fragment>
          );
        })}
        
        {/* Equity section */}
        <ALMPositionTableRow 
          item={equityTotal} 
          currentHorizons={currentHorizons} 
          isHeader 
          className="font-medium bg-muted/20"
        />
        
        {almData.equity.map((equity, index) => {
          // Skip rendering subcategories here since we'll handle them in the parent category logic
          if (isSubcategory(equity.name)) {
            return null;
          }
          
          const isParent = parentCategories[equity.name]?.length > 0;
          
          return (
            <React.Fragment key={`equity-${index}`}>
              <TableRow 
                key={`equity-${index}`}
                className="cursor-pointer hover:bg-muted/40"
                onClick={() => toggleCategory(`equity-${equity.name}`)}
              >
                <TableCell className={isParent ? "font-semibold" : "pl-6 text-muted-foreground"}>
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6 p-0"
                    >
                      {expandedCategories[`equity-${equity.name}`] ? 
                        <ChevronDown className="h-4 w-4" /> : 
                        <ChevronRight className="h-4 w-4" />}
                    </Button>
                    <span>{equity.name}</span>
                  </div>
                </TableCell>
                
                {currentHorizons.map(horizon => (
                  <TableCell 
                    key={horizon.id} 
                    className={`text-right ${isParent ? "font-semibold" : "text-muted-foreground"}`}
                  >
                    {formatCurrency(equity.values[horizon.id] || 0)}
                  </TableCell>
                ))}
              </TableRow>
              
              {/* Show subcategories when equity category is expanded */}
              {expandedCategories[`equity-${equity.name}`] && isParent && (
                <>
                  {parentCategories[equity.name]?.map((childName, childIndex) => {
                    const childItem = almData.equity.find(item => item.name === childName);
                    if (!childItem) return null;
                    
                    return (
                      <TableRow 
                        key={`equity-child-${childIndex}`}
                        className="hover:bg-muted/40"
                      >
                        <TableCell className="pl-12 text-muted-foreground">
                          {childName}
                        </TableCell>
                        
                        {currentHorizons.map(horizon => (
                          <TableCell 
                            key={horizon.id} 
                            className="text-right text-muted-foreground"
                          >
                            {formatCurrency(childItem.values[horizon.id] || 0)}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </>
              )}
            </React.Fragment>
          );
        })}
        
        {/* Net Position row */}
        <NetPositionRow 
          totals={totals} 
          currentHorizons={currentHorizons} 
          onOpenBorrowSheet={onOpenBorrowSheet}
        />
      </TableBody>
    </Table>
  );
};

// Helper function to map categories to contract types
const getContractsForCategory = (
  category: string, 
  contractsByType: Record<string, ALMContract[]>
): ALMContract[] => {
  // Map specific categories to their corresponding contract types
  const categoryToContractTypeMap: Record<string, string> = {
    // Asset categories
    "Vehicle Loans": "Loan Asset",
    "Personal Loans": "Loan Asset",
    "SME Loans": "Loan Asset", 
    "Gold Loans": "Loan Asset",
    "Loan Assets": "Loan Asset",
    "Bank FDs": "Fixed Deposit",
    "Corporate FDs": "Fixed Deposit",
    "Certificate of Deposits": "Fixed Deposit",
    "Treasury Bills": "Fixed Deposit",
    "Fixed Deposits": "Fixed Deposit",
    
    // Liability categories
    "Secured NCDs": "NCD",
    "Unsecured NCDs": "NCD",
    "Market Linked NCDs": "NCD",
    "Non-Convertible Debentures": "NCD",
    "Bank Term Loans": "Term Loan",
    "NBFC Term Loans": "Term Loan",
    "External Commercial Borrowings": "Term Loan",
    "Refinance Loans": "Term Loan",
    "Term Loans": "Term Loan",
    "Cash Credit": "Working Capital",
    "Overdraft Facilities": "Working Capital",
    "Short-term Credit Lines": "Working Capital",
    "Working Capital Loans": "Working Capital"
  };
  
  const contractType = categoryToContractTypeMap[category];
  if (contractType) {
    // Filter contracts by both type and subtype
    const allContracts = contractsByType[contractType] || [];
    
    // For loan assets, filter by subtype
    if (contractType === "Loan Asset" && category !== "Loan Assets") {
      return allContracts.filter(contract => contract.subtype === category);
    }
    
    return allContracts;
  }
  return [];
};

export default ALMPositionTable;
